"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect, useRef } from "react"
import { useAuth } from "./AuthContext"

interface ApiContextType {
  baseUrl: string | null
  apiUrl: string | null
  hierarchyUrl: (assetName: string) => string
  makeAbsoluteUrl: (relativeUrl: string) => string
}

const ApiContext = createContext<ApiContextType | undefined>(undefined)

export function useApi() {
  const context = useContext(ApiContext)
  if (!context) {
    throw new Error("useApi must be used within an ApiProvider")
  }
  return context
}

export function ApiProvider({ children }: { children: React.ReactNode }) {
  const { apiEndpoint, user } = useAuth()
  const [baseUrl, setBaseUrl] = useState<string | null>(null)
  const [apiUrl, setApiUrl] = useState<string | null>(null)
  const previousEndpointRef = useRef<string | null>(null)
  const previousUserRef = useRef<string | null>(null)

  useEffect(() => {
    // Only update if the endpoint or user has changed
    const currentUserId = user?.uid || null

    if (apiEndpoint !== previousEndpointRef.current || currentUserId !== previousUserRef.current) {
      console.log("ApiContext: Updating with new endpoint or user", {
        apiEndpoint,
        userId: currentUserId,
        previousEndpoint: previousEndpointRef.current,
        previousUser: previousUserRef.current,
      })

      if (apiEndpoint) {
        // Extract the base URL from the API endpoint
        // Example: https://ynikhil.pythonanywhere.com/api/ -> https://ynikhil.pythonanywhere.com
        try {
          const url = new URL(apiEndpoint)

         // const dummyUrl = new URL("https://tams.degitrans.com");
          const safeBaseUrl  = `${url.protocol}//${url.hostname}${url.port ? `:${url.port}` : ""}`
         
          //const baseUrl = `${url.protocol}//${url.hostname}${url.port ? `:${url.port}` : ""}`
          //const baseUrl = `${url.protocol}//${url.hostname}`
          // const baseUrl =  (url.protocol ? url.protocol : "https:") + "//" + (url.hostname ? url.hostname : "") +
          //                 (url.port ? `:${url.port}` : "");
          console.log("ApiContext: Parsed URL", {
            apiEndpoint,
            safeBaseUrl,
            hostname: url.hostname,
          })

          setBaseUrl(safeBaseUrl)
          setApiUrl(apiEndpoint)
        } catch (error) {
          console.error("ApiContext: Error parsing URL", { apiEndpoint, error })
          setBaseUrl(null)
          setApiUrl(null)
        }
      } else {
        setBaseUrl(null)
        setApiUrl(null)
      }

      // Update refs to current values
      previousEndpointRef.current = apiEndpoint
      previousUserRef.current = currentUserId
    }
  }, [apiEndpoint, user])

  // Function to create a hierarchy URL for a specific asset
  const hierarchyUrl = (assetName: string): string => {
    if (!apiUrl) return ""
    const formattedAssetName = assetName.replace(/ /g, "_")
    return `${apiUrl}hierarchy?asset=${formattedAssetName}`
  }

  // Function to convert relative URLs to absolute URLs
  const makeAbsoluteUrl = (relativeUrl: string): string => {
    if (!baseUrl || !relativeUrl) return relativeUrl
    if (relativeUrl.startsWith("http")) return relativeUrl

    return `${baseUrl}${relativeUrl.startsWith("/") ? "" : "/"}${relativeUrl}`
  }

  const value = {
    baseUrl,
    apiUrl,
    hierarchyUrl,
    makeAbsoluteUrl,
  }

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>
}

